import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { clampKpiRange } from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    extractKpiAxisConfigFromChartData,
    extractKpiDataRangeFromChartData,
    hasPpaChartData,
} from 'components/pages/PackArchitecture/packArchitectureChart.helpers';
import {
    getChartPageLimit,
    getEffectiveChartRowCount,
} from 'components/pages/PackArchitecture/packArchitectureChartRequest.helpers';
import type { PpaChartResponse } from 'components/pages/PackArchitecture/pricePackArchitecture.types';
import {
    type PricePackArchitectureRequest,
    useFetchPricePackArchitecture,
} from 'hooks/ppa/useFetchPricePackArchitecture';

import type { usePackArchitectureAxisConfig } from './usePackArchitectureAxisConfig';

type AxisConfigSlice = ReturnType<typeof usePackArchitectureAxisConfig>;
type RefetchReason = 'initial' | 'search' | 'other';

function toFetchRequest(
    request: NonNullable<AxisConfigSlice['chartRequest']>,
    offset: number,
): PricePackArchitectureRequest | null {
    const limit = getChartPageLimit(request.user_inputs, offset);
    if (limit <= 0) return null;

    return {
        ...request,
        offset,
        limit,
    };
}

function getTableRefetchReason(
    hasLoadedOnce: boolean,
    searchKeyword: string,
    prevSearchKey: string,
): RefetchReason {
    if (!hasLoadedOnce) return 'initial';
    if (searchKeyword !== prevSearchKey) return 'search';
    return 'other';
}

type ChartFetchState = ReturnType<typeof useFetchPricePackArchitecture>;

function useResolvedTableSource(
    hasTableSearch: boolean,
    chartFetch: ChartFetchState,
    tableFetch: ChartFetchState,
    chartRequest: AxisConfigSlice['chartRequest'],
    tableRequest: AxisConfigSlice['tableRequest'],
) {
    const tableRows = hasTableSearch ? tableFetch.rows : chartFetch.rows;
    const tableTotalCount = hasTableSearch ? tableFetch.totalCount : chartFetch.totalCount;
    const isTableFetchLoading = hasTableSearch ? tableFetch.loading : chartFetch.loading;
    const isTablePaginatedLoading = hasTableSearch
        ? tableFetch.isPaginatedLoading
        : chartFetch.isPaginatedLoading;
    const tableFetchError = hasTableSearch ? tableFetch.error : chartFetch.error;
    const tableEffectiveRowCount = getEffectiveChartRowCount(
        (hasTableSearch ? tableRequest : chartRequest)?.user_inputs,
        tableTotalCount,
    );
    const tableHasMorePages = tableRows.length > 0 && tableRows.length < tableEffectiveRowCount;

    return {
        tableRows,
        tableTotalCount,
        isTableFetchLoading,
        isTablePaginatedLoading,
        tableFetchError,
        tableEffectiveRowCount,
        tableHasMorePages,
    };
}

function useChartRequestFetch(
    chartRequest: AxisConfigSlice['chartRequest'],
    globalFilterQueryKey: AxisConfigSlice['globalFilterQueryKey'],
    skipNextChartFetchRef: React.MutableRefObject<boolean>,
    hasTableSearchRef: React.MutableRefObject<boolean>,
    hasLoadedTableOnceRef: React.MutableRefObject<boolean>,
    setTableRefetchReason: React.Dispatch<React.SetStateAction<RefetchReason | null>>,
    fetchChartData: ChartFetchState['fetchPricePackArchitecture'],
) {
    const [isChartRequestTransitioning, setIsChartRequestTransitioning] = useState(false);
    const chartFetchRequestIdRef = useRef(0);
    const lastChartFetchKeyRef = useRef<string | null>(null);
    const lastGlobalFilterQueryKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!chartRequest) {
            lastChartFetchKeyRef.current = null;
            lastGlobalFilterQueryKeyRef.current = null;
            setIsChartRequestTransitioning(false);
            return;
        }

        const initialRequest = toFetchRequest(chartRequest, 0);
        if (!initialRequest) {
            setIsChartRequestTransitioning(false);
            return;
        }

        const globalFilterChanged = lastGlobalFilterQueryKeyRef.current !== globalFilterQueryKey;
        if (globalFilterChanged) {
            lastGlobalFilterQueryKeyRef.current = globalFilterQueryKey;
        }

        const fetchKey = JSON.stringify(initialRequest);
        if (!globalFilterChanged && lastChartFetchKeyRef.current === fetchKey) {
            return;
        }

        if (!globalFilterChanged && skipNextChartFetchRef.current) {
            skipNextChartFetchRef.current = false;
            lastChartFetchKeyRef.current = fetchKey;
            return;
        }

        lastChartFetchKeyRef.current = fetchKey;

        if (!hasTableSearchRef.current) {
            setTableRefetchReason(!hasLoadedTableOnceRef.current ? 'initial' : 'other');
        }

        const requestId = ++chartFetchRequestIdRef.current;
        setIsChartRequestTransitioning(true);

        void fetchChartData(initialRequest).finally(() => {
            if (chartFetchRequestIdRef.current === requestId) {
                setIsChartRequestTransitioning(false);
            }
        });
    }, [
        chartRequest,
        fetchChartData,
        globalFilterQueryKey,
        hasLoadedTableOnceRef,
        hasTableSearchRef,
        setTableRefetchReason,
        skipNextChartFetchRef,
    ]);

    return isChartRequestTransitioning;
}

function useTableSearchRequestFetch(
    hasTableSearch: boolean,
    tableRequest: AxisConfigSlice['tableRequest'],
    globalFilterQueryKey: AxisConfigSlice['globalFilterQueryKey'],
    tableSearchKeyword: string,
    hasLoadedTableOnceRef: React.MutableRefObject<boolean>,
    prevSearchKeyRef: React.MutableRefObject<string>,
    setTableRefetchReason: React.Dispatch<React.SetStateAction<RefetchReason | null>>,
    fetchTableSearchData: ChartFetchState['fetchPricePackArchitecture'],
) {
    const [isTableRequestTransitioning, setIsTableRequestTransitioning] = useState(false);
    const tableFetchRequestIdRef = useRef(0);

    useEffect(() => {
        if (!hasTableSearch) {
            setIsTableRequestTransitioning(false);
            return;
        }

        if (!tableRequest) {
            setIsTableRequestTransitioning(false);
            setTableRefetchReason(null);
            return;
        }

        const initialRequest = toFetchRequest(tableRequest, 0);
        if (!initialRequest) {
            setIsTableRequestTransitioning(false);
            setTableRefetchReason(null);
            return;
        }

        setTableRefetchReason(
            getTableRefetchReason(
                hasLoadedTableOnceRef.current,
                tableSearchKeyword,
                prevSearchKeyRef.current,
            ),
        );
        prevSearchKeyRef.current = tableSearchKeyword;

        const requestId = ++tableFetchRequestIdRef.current;
        setIsTableRequestTransitioning(true);

        void fetchTableSearchData(initialRequest).finally(() => {
            if (tableFetchRequestIdRef.current === requestId) {
                setIsTableRequestTransitioning(false);
            }
        });
    }, [
        fetchTableSearchData,
        globalFilterQueryKey,
        hasLoadedTableOnceRef,
        hasTableSearch,
        prevSearchKeyRef,
        setTableRefetchReason,
        tableRequest,
        tableSearchKeyword,
    ]);

    return isTableRequestTransitioning;
}

function useKpiAxisConfigSync(
    isXAxisKpi: boolean,
    chartResponse: PpaChartResponse | null,
    isChartFetchLoading: boolean,
    xAxisKpiDataRange: ReturnType<typeof extractKpiDataRangeFromChartData> | undefined,
    xAxisKpiConfig: AxisConfigSlice['xAxisKpiConfig'],
    setXAxisKpiConfig: AxisConfigSlice['setXAxisKpiConfig'],
    skipNextChartFetchRef: React.MutableRefObject<boolean>,
) {
    useEffect(() => {
        if (!isXAxisKpi || !chartResponse || isChartFetchLoading || !xAxisKpiDataRange || xAxisKpiConfig) {
            return;
        }

        const configFromApi = extractKpiAxisConfigFromChartData(chartResponse, xAxisKpiDataRange);
        if (configFromApi) {
            // Chart data already reflects API bin_config; syncing state must not refetch.
            skipNextChartFetchRef.current = true;
            setXAxisKpiConfig(configFromApi);
        }
    }, [
        chartResponse,
        isChartFetchLoading,
        isXAxisKpi,
        setXAxisKpiConfig,
        skipNextChartFetchRef,
        xAxisKpiConfig,
        xAxisKpiDataRange,
    ]);

    useEffect(() => {
        if (!xAxisKpiConfig || !xAxisKpiDataRange) return;

        const clamped = clampKpiRange(xAxisKpiConfig.min, xAxisKpiConfig.max, xAxisKpiDataRange);
        if (clamped.min === xAxisKpiConfig.min && clamped.max === xAxisKpiConfig.max) return;

        setXAxisKpiConfig({ ...xAxisKpiConfig, ...clamped });
    }, [setXAxisKpiConfig, xAxisKpiConfig, xAxisKpiDataRange]);
}

export function usePackArchitectureChartData(axisConfig: AxisConfigSlice) {
    const {
        xAxis,
        isXAxisKpi,
        xAxisKpiConfig,
        setXAxisKpiConfig,
        chartRequest,
        tableRequest,
        globalFilterQueryKey,
        tableSearchKeyword,
        isChartBootstrapLoading,
    } = axisConfig;

    const hasTableSearch = tableSearchKeyword.trim().length > 0;
    const hasTableSearchRef = useRef(hasTableSearch);
    hasTableSearchRef.current = hasTableSearch;

    const chartFetch = useFetchPricePackArchitecture();
    const tableFetch = useFetchPricePackArchitecture();

    const {
        data: chartData,
        rows: chartRows,
        loading: isChartFetchLoading,
        isPaginatedLoading: isChartPaginatedLoading,
        error: chartError,
        totalCount: chartTotalCount,
        fetchPricePackArchitecture: fetchChartData,
    } = chartFetch;

    const { fetchPricePackArchitecture: fetchTableSearchData } = tableFetch;

    const hasLoadedTableOnceRef = useRef(false);
    const prevSearchKeyRef = useRef('');
    const skipNextChartFetchRef = useRef(false);
    const [tableRefetchReason, setTableRefetchReason] = useState<RefetchReason | null>(null);

    const {
        tableRows,
        isTableFetchLoading,
        isTablePaginatedLoading,
        tableFetchError,
        tableEffectiveRowCount,
        tableHasMorePages,
    } = useResolvedTableSource(hasTableSearch, chartFetch, tableFetch, chartRequest, tableRequest);

    const chartResponse = chartData as PpaChartResponse | null;
    const isAwaitingChartResponse = Boolean(chartRequest) && chartData === null && !chartError;
    const isChartPanelLoading =
        isChartBootstrapLoading || isChartFetchLoading || isAwaitingChartResponse;

    const xAxisKpiDataRange = useMemo(() => {
        if (!isXAxisKpi || !chartResponse || isChartFetchLoading) return undefined;
        return extractKpiDataRangeFromChartData(chartResponse, xAxis);
    }, [chartResponse, isChartFetchLoading, isXAxisKpi, xAxis]);

    const chartEffectiveRowCount = getEffectiveChartRowCount(chartRequest?.user_inputs, chartTotalCount);

    const isChartRequestTransitioning = useChartRequestFetch(
        chartRequest,
        globalFilterQueryKey,
        skipNextChartFetchRef,
        hasTableSearchRef,
        hasLoadedTableOnceRef,
        setTableRefetchReason,
        fetchChartData,
    );

    const isTableRequestTransitioning = useTableSearchRequestFetch(
        hasTableSearch,
        tableRequest,
        globalFilterQueryKey,
        tableSearchKeyword,
        hasLoadedTableOnceRef,
        prevSearchKeyRef,
        setTableRefetchReason,
        fetchTableSearchData,
    );

    const isChartTableRefetching =
        isChartRequestTransitioning ||
        isTableRequestTransitioning ||
        (isTableFetchLoading && !isTablePaginatedLoading);

    const isChartTableLoaderRefetching =
        isChartTableRefetching && tableRefetchReason != null;

    useEffect(() => {
        if (tableRows.length > 0) {
            hasLoadedTableOnceRef.current = true;
        }
    }, [tableRows.length]);

    useEffect(() => {
        if (!isChartTableRefetching) {
            setTableRefetchReason(null);
        }
    }, [isChartTableRefetching]);

    useKpiAxisConfigSync(
        isXAxisKpi,
        chartResponse,
        isChartFetchLoading,
        xAxisKpiDataRange,
        xAxisKpiConfig,
        setXAxisKpiConfig,
        skipNextChartFetchRef,
    );

    const chartHasMorePages = chartRows.length > 0 && chartRows.length < chartEffectiveRowCount;

    const fetchNextPricePackArchitecturePage = useCallback(async () => {
        if (isChartFetchLoading || !chartHasMorePages || !chartRequest) return;

        const nextRequest = toFetchRequest(chartRequest, chartRows.length);
        if (!nextRequest) return;

        await fetchChartData(nextRequest);
    }, [chartHasMorePages, chartRequest, chartRows.length, fetchChartData, isChartFetchLoading]);

    const fetchNextTablePage = useCallback(async () => {
        if (isTableFetchLoading || !tableHasMorePages) return;

        const activeRequest = hasTableSearch ? tableRequest : chartRequest;
        if (!activeRequest) return;

        const nextRequest = toFetchRequest(activeRequest, tableRows.length);
        if (!nextRequest) return;

        const fetch = hasTableSearch ? fetchTableSearchData : fetchChartData;
        await fetch(nextRequest);
    }, [
        chartRequest,
        fetchChartData,
        fetchTableSearchData,
        hasTableSearch,
        isTableFetchLoading,
        tableHasMorePages,
        tableRequest,
        tableRows.length,
    ]);

    return {
        rows: chartRows,
        tableRows,
        isChartLoading: isChartFetchLoading,
        isChartTableRefetching,
        isChartTableLoaderRefetching,
        isChartPaginatedLoading,
        isTablePaginatedLoading,
        isChartPanelLoading,
        isChartError: Boolean(chartError),
        hasChartData: hasPpaChartData(chartResponse),
        chartError,
        tableFetchError,
        fetchNextPricePackArchitecturePage,
        fetchNextTablePage,
        hasMorePages: chartHasMorePages,
        tableHasMorePages,
        totalCount: chartTotalCount,
        effectiveRowCount: tableEffectiveRowCount,
        xAxisKpiDataRange,
    };
}
